import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { mapOpsiumUserProfileFromResponse } from '@/modules/common/mappers/opsium-user-profile.mapper';
import { UpdateOpsiumUserProfileRequest } from '@/modules/common/types/dto/request/update-opsium-user-profile-request';
import { OpsiumUserProfileResponse } from '@/modules/common/types/dto/response/opsium-user-profile-response';
import { MyOpsiumUserProfile } from '@/modules/common/types/opsium-user-profile';
import { axiosInstance } from '@/utils/axios';

/**
 * Fetches current user's profile information
 * Returns user ID, email, name, profile image URL, and decrypted policies
 */
export const getUserProfile = async (): Promise<MyOpsiumUserProfile> => {
  const endpoint = '/api/opsium-users/me/profile';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.get<{ data: OpsiumUserProfileResponse }>(endpoint)
  );

  return mapOpsiumUserProfileFromResponse(response);
};

/**
 * Updates current user's profile information
 * Currently only supports updating the name field
 */
export const updateUserProfile = async (
  data: UpdateOpsiumUserProfileRequest
): Promise<MyOpsiumUserProfile> => {
  const endpoint = '/api/opsium-users/me/profile';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.patch<{ data: OpsiumUserProfileResponse }>(endpoint, data)
  );

  return mapOpsiumUserProfileFromResponse(response);
};

export const getPersonalAvatarPresignedUrl = async ({
  fileName,
  contentLength,
}: {
  fileName: string;
  contentLength: number;
}): Promise<string> => {
  const endpoint = '/api/opsium-users/me/profile/avatar-presigned-url';

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.PROFILE_AVATAR_UPLOAD_FAILED,
    () =>
      axiosInstance.get<{
        data: { presignedUrl: string };
      }>(endpoint, {
        params: { fileName, contentLength },
      })
  );

  return response.presignedUrl;
};

export const deletePersonalAvatar = async (): Promise<void> => {
  const endpoint = '/api/opsium-users/me/profile/avatar';

  await withApiErrorHandler(endpoint, ApiErrorCode.PROFILE_AVATAR_DELETE_FAILED, () =>
    axiosInstance.delete(endpoint)
  );
};
